import { useEffect, useState } from "react";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";

import { Spinner, useToast } from "../components";
import { DecimalInput, ErrorNote, Field } from "../components";
import { useDoc } from "../hooks";
import { db, auth } from "../lib/firebase";
import { formatDecimalInput, parseDecimal } from "../lib/decimal";

/**
 * TESLİM SÜRESİ AYARLARI — `settings/delivery`.
 *
 * ── NİÇİN AYRI DOKÜMAN ─────────────────────────────────────────────────────
 * `settings/finance` yalnızca `costView` olana açıktır (KDV, komisyon, kargo
 * birim fiyatı gibi maliyet parametreleri içerir). Teslim süresi ise MALİYET
 * DEĞİL, operasyonel bir vaattir; üretim ve ofis rolünün de görmesi gerekir —
 * "bu ürün kaç günde çıkar" sorusunu atölye de soruyor. Ayrı doküman, ayrı
 * gizlilik sınırı demek.
 *
 * ── NİÇİN BU EKRAN VAR ─────────────────────────────────────────────────────
 * Ürün kartındaki teslim süresi kutusu "Ayarlardaki varsayılan" yazıyordu ama
 * Ayarlar ekranında böyle bir alan YOKTU. Değer veriye yazılmıştı, kullanıcı
 * onu ne görebiliyor ne değiştirebiliyordu — etiket, var olmayan bir yeri
 * işaret ediyordu.
 */

interface DeliverySettingsDoc {
  leadTimeMadeToOrder?: number;
  leadTimeMadeToStock?: number;
}

const DEFAULTS: Required<DeliverySettingsDoc> = {
  leadTimeMadeToOrder: 10,
  leadTimeMadeToStock: 1,
};

export function DeliveryForm() {
  const toast = useToast();
  const { data, loading, error } = useDoc<DeliverySettingsDoc>("settings/delivery");

  const [madeToOrder, setMadeToOrder] = useState("");
  const [madeToStock, setMadeToStock] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const s = data ?? DEFAULTS;
    setMadeToOrder(formatDecimalInput(s.leadTimeMadeToOrder ?? DEFAULTS.leadTimeMadeToOrder));
    setMadeToStock(formatDecimalInput(s.leadTimeMadeToStock ?? DEFAULTS.leadTimeMadeToStock));
  }, [data]);

  async function save() {
    setErr(null);

    const toOrder = parseDecimal(madeToOrder);
    const toStock = parseDecimal(madeToStock);

    // ★ Sessiz kabul yok: geçersiz değer "0 gün"e dönüşüp müşteriye imkânsız bir
    // söz verdirmemeli.
    // `parseDecimal` boş/bozuk girdide null döner — o durumu sayı gibi
    // işlemek "0 iş günü" gibi imkânsız bir vaade dönüşürdü.
    for (const [v, label] of [
      [toOrder, "Sipariş üzerine üretim"],
      [toStock, "Stoğa üretim"],
    ] as const) {
      if (v === null || !Number.isFinite(v) || v < 0 || v > 365) {
        setErr(`${label} süresi 0 ile 365 iş günü arasında bir sayı olmalı.`);
        return;
      }
    }
    if (toStock! > toOrder!) {
      setErr(
        "Stoktan gönderim süresi, sipariş üzerine üretim süresinden uzun olamaz — " +
          "stoğa üretmenin amacı daha hızlı göndermektir.",
      );
      return;
    }

    const uid = auth.currentUser?.uid;
    if (!uid) {
      setErr("Oturum bulunamadı. Lütfen yeniden giriş yapın.");
      return;
    }

    setBusy(true);
    try {
      await setDoc(
        doc(db, "settings", "delivery"),
        {
          leadTimeMadeToOrder: toOrder!,
          leadTimeMadeToStock: toStock!,
          /**
           * ★ OLUŞTURMA DAMGALARI EKSİKTİ.
           *
           * Doküman HENÜZ YOKKEN bu ekran onu yaratamıyordu: Rules
           * `allow create` şartı `createStamp()` — yani `createdAt`, `createdBy`,
           * `updatedAt`, `updatedBy` ve `deleted == false` — arıyor; bu yazma
           * ise yalnızca iki süreyi ve güncelleme damgasını gönderiyordu.
           * Ekran bugün çalışıyor gibi görünmesinin tek sebebi, `settings/delivery`
           * dokümanının konsoldan elle açılmış olması (canlı veride `createdAt`
           * ve `createdBy` alanları yok — bu ekran yazmadı çünkü yazamazdı).
           * Doküman silinse/temiz bir ortama kurulsa "izin yok" hatası verirdi.
           * `Settings.tsx` içindeki finans formu bu durumu zaten doğru ele alıyordu.
           */
          ...(data ? {} : { createdAt: serverTimestamp(), createdBy: uid, deleted: false }),
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      toast.success("Teslim süreleri kaydedildi.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Kaydedilemedi.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner />;

  return (
    <section className="mt-8">
      <h2 className="text-base font-semibold text-gray-900">Teslim Süreleri</h2>
      <p className="mt-1 max-w-3xl text-sm text-gray-600">
        Ürün kartındaki "Teslim süresi" bu değerleri kullanır. Bir ürüne özel süre
        girilirse o kazanır. Bu ayar <strong>satışı değil teslim vaadini</strong>{" "}
        belirler — her üründen sipariş alınmaya devam edilir.
      </p>

      <ErrorNote error={err ?? error} />

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="Sipariş üzerine üretim (iş günü)"
          hint="Stok tutulmayan ürünler. Kataloğun büyük kısmı böyledir."
        >
          <DecimalInput
            value={madeToOrder}
            onChange={setMadeToOrder}
            className="max-w-[10rem]"
          />
        </Field>

        <Field
          label="Stoğa üretim / hızlı gönderim (iş günü)"
          hint="Stokta hazır bekleyen ürünler. Stok bittiğinde üstteki süre geçerli olur."
        >
          <DecimalInput
            value={madeToStock}
            onChange={setMadeToStock}
            className="max-w-[10rem]"
          />
        </Field>
      </div>

      <div className="mt-4">
        <button type="button" onClick={save} disabled={busy} className="btn-primary">
          {busy ? "Kaydediliyor…" : "Teslim Sürelerini Kaydet"}
        </button>
      </div>
    </section>
  );
}

export default DeliveryForm;
